import {
  cardMultiplier,
  cardScore,
  selectionLabel,
  CONFIG,
  type Fixture,
  type MarketTeams,
  type Outcome,
  type Prediction
} from '@h2h/shared';
import type { DuelSideDuelPick, DuelSidePrediction } from '../../lib/api';
import { shortRider } from '../../lib/cyclingRound';

/** Top weight on the cycling ladder — the "banker" duel (premium pick). */
export const CYC_MAX_WEIGHT = Math.max(...CONFIG.CYCLING.CONFIDENCE_WEIGHTS);

export function teamCode(name: string | undefined, code?: string): string {
  if (code) return code;
  if (!name) return '?';
  return name.replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase() || name.slice(0, 3).toUpperCase();
}

export function isRevealed(p: DuelSidePrediction): p is DuelSidePrediction & { outcome: Outcome } {
  return !p.locked && p.outcome != null;
}

/** The card multiplier for a revealed pick — its ×N boost stack. */
export function pickMultiplier(p: DuelSidePrediction): number {
  return cardMultiplier(p.legs ?? []);
}

/** The signed card score for a revealed pick whose fixture is final (Build-Your-Card
 *  §7). Uses the stamped `cardScore` when present, else recomputes via the shared
 *  engine so the duel board matches settlement exactly. */
export function pickPoints(p: DuelSidePrediction, f: Fixture | undefined): number | null {
  if (!isRevealed(p) || !f || f.status !== 'SETTLED' || !f.result) return null;
  const pred: Prediction = { fixtureId: p.fixtureId, outcome: p.outcome, legs: p.legs ?? [] };
  return p.cardScore ?? cardScore(pred, f.result);
}

export function pickSummary(p: DuelSidePrediction, teams: MarketTeams): string {
  if (p.locked || !isRevealed(p)) return 'Locked';
  const base = selectionLabel('outcome', p.outcome, teams);
  const boosts = p.legs?.length ?? 0;
  return boosts > 0 ? `${base} +${boosts}` : base;
}

/** The rider a side backed, as a compact surname (null when locked / no pick). */
export function backedRider(pick: DuelSideDuelPick | undefined, aName: string, bName: string): string | null {
  if (!pick || pick.locked || !pick.pick) return null;
  return shortRider(pick.pick === 'A' ? aName : bName);
}
